import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import DialogModal from '../../components/DialogModal';
import hostelService from '../../services/hostelService';
import studentsService from '../../services/studentsService';
import { MagnifyingGlass, Plus, PencilSimple, Trash, ArrowLeft } from '@phosphor-icons/react';
import './hostel.css';

function ComplaintsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [complaints, setComplaints] = useState([]);
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [students, setStudents] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    hostel_id: '',
    student_id: '',
    title: '',
    complaint_type: 'Maintenance',
    description: '',
    priority: 'medium',
    status: 'open',
    reported_date: new Date().toISOString().split('T')[0],
    resolution_notes: ''
  });

  const filterComplaints = React.useCallback(() => {
    let filtered = complaints;

    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    setFilteredComplaints(filtered);
  }, [complaints, searchTerm, statusFilter]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterComplaints();
  }, [complaints, searchTerm, statusFilter, filterComplaints]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [complaintsData, studentsData, hostelsData] = await Promise.all([
        hostelService.listComplaints().catch(() => []),
        studentsService.listStudents().catch(() => []),
        hostelService.listHostels().catch(() => [])
      ]).catch(() => [[], [], []]);

      // Handle complaints response
      let complaintsArray = [];
      if (Array.isArray(complaintsData)) {
        complaintsArray = complaintsData;
      } else if (complaintsData.complaints) {
        complaintsArray = complaintsData.complaints;
      } else if (complaintsData.items) {
        complaintsArray = complaintsData.items;
      }

      // Handle students response
      let studentsArray = [];
      if (Array.isArray(studentsData)) {
        studentsArray = studentsData;
      } else if (studentsData.students) {
        studentsArray = studentsData.students;
      } else if (studentsData.items) {
        studentsArray = studentsData.items;
      }

      // Handle hostels response
      let hostelsArray = [];
      if (Array.isArray(hostelsData)) {
        hostelsArray = hostelsData;
      } else if (hostelsData.hostels) {
        hostelsArray = hostelsData.hostels;
      } else if (hostelsData.items) {
        hostelsArray = hostelsData.items;
      }

      setComplaints(complaintsArray);
      setStudents(studentsArray);
      setHostels(hostelsArray);
      
      // Set default hostel_id if hostels are available
      if (hostelsArray.length > 0 && !formData.hostel_id) {
        setFormData(prev => ({ ...prev, hostel_id: hostelsArray[0].id }));
      }
    } catch (error) {
      console.error('Error loading complaints:', error);
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.student_id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : 'Unknown';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.hostel_id || !formData.student_id || !formData.title || !formData.description) {
        toast.error('Please fill in all required fields');
        return;
      }

      if (editingId) {
        // Backend only supports updating status and resolution_notes
        await hostelService.updateComplaintStatus(editingId, formData.status, formData.resolution_notes || undefined);
        toast.success('Complaint updated successfully');
      } else {
        await hostelService.createComplaint(formData);
        toast.success('Complaint created successfully');
      }
      setShowModal(false);
      setEditingId(null);
      setFormData({
        hostel_id: hostels.length > 0 ? hostels[0].id : '',
        student_id: '',
        title: '',
        complaint_type: 'Maintenance',
        description: '',
        priority: 'medium',
        status: 'open',
        reported_date: new Date().toISOString().split('T')[0],
        resolution_notes: ''
      });
      loadData();
    } catch (error) {
      console.error('Error saving complaint:', error);
      toast.error(editingId ? 'Failed to update complaint' : 'Failed to create complaint');
    }
  };

  const handleEdit = (complaint) => {
    setEditingId(complaint.id);
    setFormData({
      hostel_id: complaint.hostel_id,
      student_id: complaint.student_id,
      title: complaint.title,
      complaint_type: complaint.complaint_type,
      description: complaint.description,
      priority: complaint.priority,
      status: complaint.status,
      reported_date: complaint.reported_date,
      resolution_notes: complaint.resolution_notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteTarget(id);
  };

  const confirmDeleteComplaint = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await hostelService.deleteComplaint(deleteTarget);
      toast.success('Complaint deleted successfully');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      console.error('Error deleting complaint:', error);
      toast.error('Failed to delete complaint');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return <Layout><div className="loading">Loading complaints...</div></Layout>;
  }

  const openComplaints = complaints.filter(c => c.status === 'open').length;
  const resolvedComplaints = complaints.filter(c => c.status === 'resolved').length;

  return (
    <Layout>
      <div className="page hostel-page">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/hostel')} title="Back to Hostel">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Student Complaints</h1>
              <p>Manage and track student complaints and issues</p>
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditingId(null);
              setFormData({
                hostel_id: hostels.length > 0 ? hostels[0].id : '',
                student_id: '',
                title: '',
                complaint_type: 'Maintenance',
                description: '',
                priority: 'medium',
                status: 'open',
                reported_date: new Date().toISOString().split('T')[0],
                resolution_notes: ''
              });
              setShowModal(true);
            }}
          >
            <Plus size={20} />
            New Complaint
          </button>
        </div>

        <div className="stats-bar">
          <div className="stat">
            <h3>Open</h3>
            <p className="stat-value">{openComplaints}</p>
          </div>
          <div className="stat">
            <h3>Resolved</h3>
            <p className="stat-value">{resolvedComplaints}</p>
          </div>
          <div className="stat">
            <h3>Total</h3>
            <p className="stat-value">{complaints.length}</p>
          </div>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search complaints..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>

        <div className="table-container">
          {filteredComplaints.length === 0 ? (
            <div className="empty-state">
              <p>No complaints found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Reported Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredComplaints.map((complaint) => (
                  <tr key={complaint.id}>
                    <td>{getStudentName(complaint.student_id)}</td>
                    <td>{complaint.title}</td>
                    <td className="text-capitalize">{complaint.complaint_type}</td>
                    <td>
                      <span className={`badge badge-${complaint.priority === 'high' ? 'danger' : complaint.priority === 'medium' ? 'warning' : 'success'}`}>
                        {complaint.priority}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${complaint.status === 'open' ? 'warning' : complaint.status === 'resolved' ? 'success' : 'info'}`}>
                        {complaint.status}
                      </span>
                    </td>
                    <td>{complaint.reported_date}</td>
                    <td className="action-buttons">
                      <button
                        className="btn-icon btn-primary"
                        title="Edit"
                        onClick={() => handleEdit(complaint)}
                      >
                        <PencilSimple size={18} />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        title="Delete"
                        onClick={() => handleDelete(complaint.id)}
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Complaints Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>{editingId ? 'Edit Complaint' : 'New Complaint'}</h2>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                {!editingId ? (
                  <>
                    <div className="form-group">
                      <label>Hostel *</label>
                      <select
                        name="hostel_id"
                        value={formData.hostel_id}
                        onChange={(e) => setFormData({ ...formData, hostel_id: e.target.value })}
                        required
                      >
                        <option value="">Select Hostel</option>
                        {hostels.map(h => (
                          <option key={h.id} value={h.id}>
                            {h.hostel_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Student *</label>
                      <select
                        name="student_id"
                        value={formData.student_id}
                        onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                        required
                      >
                        <option value="">Select Student</option>
                        {students.map(s => (
                          <option key={s.id} value={s.student_id}>
                            {s.first_name} {s.last_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Title *</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="e.g., Broken bed frame"
                        required
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Type *</label>
                        <select
                          name="complaint_type"
                          value={formData.complaint_type}
                          onChange={(e) => setFormData({ ...formData, complaint_type: e.target.value })}
                          required
                        >
                          <option value="Noise">Noise</option>
                          <option value="Maintenance">Maintenance</option>
                          <option value="Cleanliness">Cleanliness</option>
                          <option value="Behavior">Behavior</option>
                          <option value="Safety">Safety</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Priority *</label>
                        <select
                          name="priority"
                          value={formData.priority}
                          onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                          required
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Description *</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Describe the complaint in detail"
                        required
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Reported Date *</label>
                        <input
                          type="date"
                          value={formData.reported_date}
                          onChange={(e) => setFormData({ ...formData, reported_date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          name="status"
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label><strong>Student:</strong> {formData.title ? getStudentName(formData.student_id) : 'Unknown'}</label>
                    </div>

                    <div className="form-group">
                      <label><strong>Title:</strong> {formData.title}</label>
                    </div>

                    <div className="form-group">
                      <label><strong>Type:</strong> {formData.complaint_type}</label>
                    </div>

                    <div className="form-group">
                      <label><strong>Priority:</strong> {formData.priority}</label>
                    </div>

                    <div className="form-group">
                      <label><strong>Description:</strong></label>
                      <p style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                        {formData.description}
                      </p>
                    </div>

                    <div className="form-group">
                      <label>Status *</label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        required
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Resolution Notes</label>
                      <textarea
                        value={formData.resolution_notes}
                        onChange={(e) => setFormData({ ...formData, resolution_notes: e.target.value })}
                        placeholder="Add notes about the resolution..."
                      />
                    </div>
                  </>
                )}

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingId ? 'Update' : 'Create'} Complaint
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ComplaintsPage;
